import * as Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import merge from 'lodash/fp/merge'
import cloneDeep from 'lodash/fp/cloneDeep'
import * as constants from '@adc/constants'
import * as common from './common'
import * as types from './types'
import { usesProxy, generateProxyRoute } from './proxyRoutes'

const getProgressErrorMessage = (responseText: string): string => {
  let message = ''

  try {
    const parsedResponse =
      responseText.length > 0 ? JSON.parse(responseText) : {}
    message = parsedResponse?.message
  } catch (parseError) {}

  return message
}

interface WithProgressFetchOptions {
  method?: types.HTTPMethod
  isUpload?: boolean
  headers?: Record<string, string>
  queryParams?: Record<string, string>
  onProgress?: (currentPercentage: number) => void
  responseType?: XMLHttpRequestResponseType
  body?: BodyInit
}

const defaultProgressOptions = Object.freeze({
  method: 'get',
  isUpload: false,
  headers: {
    Accept: 'application/json',
  },
  queryParams: {},
})

export default function (
  url: string,
  passedOptions: WithProgressFetchOptions = {}
): Promise<any> {
  let options: WithProgressFetchOptions = merge(
    cloneDeep(defaultProgressOptions),
    passedOptions
  )

  /**
   * Generate this on the client so it can be traced through the services
   */
  ;(options.headers as types.StringIndexable)[
    constants.headers.CORRELATION_HEADER
  ] = uuidv4()

  if (usesProxy(url)) {
    url = generateProxyRoute(url).route
  }

  /**
   * XSRF protection
   */
  const xsrfValue = Cookies.get(constants.cookies.XSRF_COOKIE)

  if (xsrfValue) {
    ;(options.headers as types.StringIndexable)[constants.headers.XSRF_HEADER] =
      xsrfValue
  }

  return new Promise(function (resolve, reject) {
    const xhr = new window.XMLHttpRequest()
    let params: BodyInit | null = new URLSearchParams(
      common.serializeParams(options.queryParams || {})
    )

    url = common.constructUrl(url, options.queryParams)

    xhr.open(options.method as string, url)
    xhr.responseType = options.responseType || xhr.responseType

    /* All XHR Events */
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        const body =
          typeof xhr.response === 'string'
            ? JSON.parse(xhr.response)
            : xhr.response

        resolve({
          status: this.status,
          body,
        })
      } else {
        /* eslint-disable prefer-promise-reject-errors */
        reject({
          status: this.status,
          body: xhr.statusText,
          message: getProgressErrorMessage(xhr.responseText),
        })
      }
    }

    xhr.onerror = function () {
      /* eslint-disable prefer-promise-reject-errors */
      reject({
        status: this.status,
        body: xhr.statusText,
        message: getProgressErrorMessage(xhr.responseText),
      })
    }

    xhr.upload.onprogress = e => {
      if (e.lengthComputable) {
        const percentage = Math.round((e.loaded / e.total) * 100)
        if (options.onProgress) options.onProgress(percentage)
      }
    }

    if (options.headers) {
      Object.entries(options.headers).forEach(([key, val]) => {
        xhr.setRequestHeader(key, val)
      })
    }

    // In the case where we have an upload, we'd like to use FormData
    //
    // NOTE: Skipping coverage for first if branch because we don't have Blob mocked in node yet.
    /* istanbul ignore if */
    if (typeof Blob !== 'undefined' && options.body instanceof Blob) {
      params = options.body
    } else if (options.isUpload) {
      params = new window.FormData()

      // @ts-ignore
      Object.entries(options.body).forEach(([key, val]) => {
        // In order for the Form object to know the key is a File, append must be given the filename
        // as the third argument
        if (key === 'file' && typeof val === 'object') {
          ;(params as FormData).append(key, val, name)
        } else {
          ;(params as FormData).append(key, val)
        }
      })
    }

    //
    // This is a little confusing because it can be a lot of things...
    //
    xhr.send(params)
  })
}
